import { useTranslation } from "react-i18next"

import { request } from "shared/api/request"
import { serializer } from "shared/api/serializer/serializer"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"
import { TeamMember } from "shared/types/__generated__/graphql"

import { TeamMembersAPIType } from "../types/TeamMember"

interface ResendInviteArgs {
  teamID: string
  teamMemberID: string
  options?: { suppressToasts?: boolean }
}

export function useResendTeamMemberInvitation() {
  const { t } = useTranslation()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.TeamMember.SEND_INVITE_EMAIL.Start"),
      success: t("Request.TeamMember.SEND_INVITE_EMAIL.Success"),
      failure: t("Request.TeamMember.SEND_INVITE_EMAIL.Failure")
    }
  })

  const resendTeamMemberInvitation = useEvent(
    async ({
      teamID,
      teamMemberID,
      options
    }: ResendInviteArgs): Promise<ApiResponse<TeamMember>> => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: !!options?.suppressToasts
      })
      try {
        const response = await request(
          `${
            import.meta.env.VITE_API_BASE
          }/project-list/teams/${teamID}/members/${teamMemberID}/invitation/send`,
          {
            method: "POST"
          }
        )
        const json = await response?.json()
        const teamMember: TeamMember = serializer.deserialize(
          TeamMembersAPIType,
          json
        )
        raiseSuccessToast()
        return {
          data: teamMember,
          errors: response?.ok
            ? undefined
            : [{ message: "Failed to resend invite" }]
        }
      } catch {
        raiseErrorToast()
        return {
          data: null,
          errors: [
            {
              message: t("Request.TeamMember.SEND_INVITE_EMAIL.Failure")
            }
          ]
        }
      }
    }
  )

  return { resendTeamMemberInvitation }
}
